/**
 * Optimizer Control Center — copy/paste clipboard hook.
 * Copy one flow's optimiser recipe, tick target rows, paste onto all of them.
 * State is in-memory (lives in the table card for its session).
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { getConfig } from '@/features/flow-optimizer/services/flow-optimizer-api';
import { optimizerCenterKeys } from '@/features/optimizer-center/hooks/useOptimizerConfigs';
import type { OptimizerRow } from '@/features/optimizer-center/optimizer-center.types';
import { useFlowOptimizerTrackers } from '@/features/trackers/flow-optimizer';
import { useToast } from '@/providers/ToastProvider';

import { extractPortableConfig } from './extract-portable-config';
import { pasteToTargets, type PasteOutcome } from './build-paste-body';
import type { CopiedOptimizerConfig, PasteFieldKey } from './optimizer-clipboard.types';

interface PasteArgs {
  targets: OptimizerRow[];
  fields: Set<PasteFieldKey>;
}

type RowKeyParts = Pick<OptimizerRow, 'provider' | 'campaignId' | 'flowId'>;

/** Stable per-row identity shared by the table, selection set and paste skip. */
export function optimizerRowKey(row: RowKeyParts): string {
  return `${row.provider}-${row.campaignId}-${row.flowId}`;
}

export interface UseOptimizerClipboard {
  copied: CopiedOptimizerConfig | null;
  copySourceKey: string | null;
  selected: Set<string>;
  isCopying: boolean;
  isPasting: boolean;
  copy: (row: OptimizerRow) => void;
  clearCopied: () => void;
  toggle: (key: string) => void;
  selectAll: (keys: string[]) => void;
  clearSelection: () => void;
  paste: (targets: OptimizerRow[], fields: Set<PasteFieldKey>) => void;
}

export function useOptimizerClipboard(): UseOptimizerClipboard {
  const toast = useToast();
  const queryClient = useQueryClient();
  const trackers = useFlowOptimizerTrackers();
  const [copied, setCopied] = useState<CopiedOptimizerConfig | null>(null);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());

  const copyMut = useMutation({
    mutationFn: (row: OptimizerRow) => getConfig(row.campaignId, row.flowId, row.provider),
    onSuccess: (config, row) => {
      if (!config) {
        toast.error('Nothing to copy', 'This flow has no saved config yet.');
        return;
      }
      setCopied({ source: rowSource(row), recipe: extractPortableConfig(config) });
      toast.success('Config copied', `From "${row.campaignName}". Tick targets, then paste.`);
    },
    onError: () => toast.error('Copy failed', 'Could not load the source config.'),
  });

  const pasteMut = useMutation({
    mutationFn: ({ targets, fields }: PasteArgs) => {
      if (!copied) throw new Error('Nothing copied');
      return pasteToTargets(copied.recipe, targets, fields);
    },
    onSuccess: (outcome) => {
      for (const provider of trackers) {
        void queryClient.invalidateQueries({ queryKey: optimizerCenterKeys.configs(provider) });
      }
      setSelected(new Set());
      announcePaste(toast, outcome);
    },
    onError: () => toast.error('Paste failed', 'No configs were updated.'),
  });

  const toggle = useCallback((key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const selectAll = useCallback((keys: string[]) => setSelected(new Set(keys)), []);
  const clearSelection = useCallback(() => setSelected(new Set()), []);
  const clearCopied = useCallback(() => {
    setCopied(null);
    setSelected(new Set());
  }, []);

  const copySourceKey = useMemo(
    () => (copied ? optimizerRowKey(copied.source) : null),
    [copied],
  );

  return {
    copied,
    copySourceKey,
    selected,
    isCopying: copyMut.isPending,
    isPasting: pasteMut.isPending,
    copy: copyMut.mutate,
    clearCopied,
    toggle,
    selectAll,
    clearSelection,
    paste: (targets, fields) => pasteMut.mutate({ targets, fields }),
  };
}

function rowSource(row: OptimizerRow): CopiedOptimizerConfig['source'] {
  return {
    provider: row.provider,
    campaignId: row.campaignId,
    flowId: row.flowId,
    campaignName: row.campaignName,
    flowName: row.flowName,
  };
}

function announcePaste(
  toast: ReturnType<typeof useToast>,
  { ok, failed }: PasteOutcome,
): void {
  if (failed === 0) toast.success('Config pasted', `Applied to ${ok} flow${ok === 1 ? '' : 's'}.`);
  else if (ok === 0) toast.error('Paste failed', `All ${failed} updates failed.`);
  else toast.warning('Partly pasted', `${ok} updated, ${failed} failed.`);
}
